import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Smartphone,
  Share,
  PlusSquare,
  MoreVertical,
  Download,
  Apple,
  CheckCircle2,
} from "lucide-react";
import {
  canTriggerNativePrompt,
  triggerNativePrompt,
  detectPlatform,
  isStandalone,
  onPromptAvailabilityChange,
} from "@/lib/installApp";
import { toast } from "sonner";

/**
 * <InstallAppButton /> — opens an installation guide modal.
 * Detects iOS vs Android vs desktop. Uses the native install prompt on
 * Chrome/Android when available; falls back to step-by-step instructions
 * on iOS Safari (which has no API for this).
 */
export const InstallAppButton = ({
  className = "",
  label = "Install Bud Link",
  testId = "install-app-btn",
  size = "default",
  variant = "primary",
}) => {
  const [open, setOpen] = useState(false);
  const [native, setNative] = useState(canTriggerNativePrompt());
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const off = onPromptAvailabilityChange((avail) => {
      setNative(avail);
      setInstalled(isStandalone());
    });
    const handler = () => setInstalled(isStandalone());
    window.addEventListener("appinstalled", handler);
    return () => {
      off();
      window.removeEventListener("appinstalled", handler);
    };
  }, []);

  if (installed) {
    return (
      <span
        data-testid="install-app-installed"
        className={`pill text-emerald-800 bg-emerald-50 border-emerald-200 ${className}`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> App installed
      </span>
    );
  }

  const styleByVariant =
    variant === "ghost"
      ? "bg-transparent border border-border text-foreground hover:bg-secondary"
      : variant === "subtle"
        ? "bg-secondary text-foreground hover:bg-secondary/80"
        : "bg-primary text-primary-foreground hover:bg-primary/90";

  return (
    <>
      <Button
        data-testid={testId}
        size={size}
        onClick={() => setOpen(true)}
        className={`rounded-full ${styleByVariant} ${className}`}
      >
        <Download className="w-4 h-4 mr-2" strokeWidth={1.7} />
        {label}
      </Button>
      <InstallGuideDialog
        open={open}
        onOpenChange={setOpen}
        native={native}
        onTriggerNative={async () => {
          const outcome = await triggerNativePrompt();
          if (outcome === "accepted") {
            toast.success("Bud Link installed");
            setOpen(false);
          } else if (outcome === "unavailable") {
            // fall through — show guide
          }
        }}
      />
    </>
  );
};

/** The instructions modal itself, also exported for use as a standalone CTA. */
export const InstallGuideDialog = ({
  open,
  onOpenChange,
  native,
  onTriggerNative,
}) => {
  const platform = detectPlatform();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl p-0 overflow-hidden"
        data-testid="install-guide-modal"
      >
        <DialogTitle className="sr-only">
          Install Bud Link on your phone
        </DialogTitle>

        <div className="px-7 pt-7 pb-5 text-center bg-secondary/40">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-foreground text-background flex items-center justify-center mb-3">
            <Smartphone className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <div className="eyebrow mb-1.5">Install</div>
          <h3 className="font-serif text-2xl text-foreground">
            Save Bud Link to your home screen
          </h3>
          <p className="text-xs text-muted-foreground mt-2 max-w-xs mx-auto leading-relaxed">
            Get one-tap access, full-screen browsing, and offline-friendly
            performance. No App Store required.
          </p>
        </div>

        <div className="p-6 space-y-5">
          {native && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-1">
                One-tap install available
              </div>
              <p className="text-sm text-foreground/80 mb-3">
                Your browser supports a one-tap install. Tap below to add Bud
                Link to your home screen.
              </p>
              <Button
                data-testid="install-guide-native"
                onClick={onTriggerNative}
                className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Install Bud Link
              </Button>
            </div>
          )}

          {platform === "ios" || platform === "unknown" ? (
            <Steps
              testIdPrefix="ios-steps"
              title="On iPhone & iPad (Safari)"
              icon={<Apple className="w-4 h-4" />}
              steps={[
                {
                  icon: <Share className="w-4 h-4" />,
                  text: (
                    <>
                      Tap the <strong>Share</strong> icon at the bottom of
                      Safari.
                    </>
                  ),
                },
                {
                  icon: <PlusSquare className="w-4 h-4" />,
                  text: (
                    <>
                      Scroll and tap <strong>Add to Home Screen</strong>.
                    </>
                  ),
                },
                {
                  icon: <CheckCircle2 className="w-4 h-4" />,
                  text: (
                    <>
                      Tap <strong>Add</strong>. Open Bud Link from your home
                      screen like any other app.
                    </>
                  ),
                },
              ]}
            />
          ) : null}

          {platform === "android" || platform === "unknown" ? (
            <Steps
              testIdPrefix="android-steps"
              title="On Android (Chrome)"
              icon={<MoreVertical className="w-4 h-4" />}
              steps={[
                {
                  icon: <MoreVertical className="w-4 h-4" />,
                  text: (
                    <>
                      Tap the <strong>⋮ menu</strong> in the top-right of
                      Chrome.
                    </>
                  ),
                },
                {
                  icon: <Download className="w-4 h-4" />,
                  text: (
                    <>
                      Tap <strong>Install app</strong> or{" "}
                      <strong>Add to Home screen</strong>.
                    </>
                  ),
                },
                {
                  icon: <CheckCircle2 className="w-4 h-4" />,
                  text: (
                    <>
                      Confirm <strong>Install</strong>. Bud Link will appear
                      next to your other apps.
                    </>
                  ),
                },
              ]}
            />
          ) : null}

          {platform === "desktop" && !native && (
            <div className="rounded-xl border border-border bg-secondary/40 p-4 text-sm text-foreground/80">
              <strong>On desktop:</strong> open this site in Chrome, Edge, or
              Brave on your phone — then tap the <em>Install</em> prompt or use
              the steps above.
            </div>
          )}

          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center pt-2">
            21+ only · Always check legality in your state
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Steps = ({ title, icon, steps, testIdPrefix }) => (
  <div data-testid={testIdPrefix} className="rounded-xl border border-border p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-foreground font-semibold mb-3">
      <span className="text-primary">{icon}</span>
      {title}
    </div>
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-3 text-sm text-foreground/85">
          <span className="mt-0.5 w-6 h-6 rounded-full bg-foreground text-background text-[11px] inline-flex items-center justify-center shrink-0">
            {i + 1}
          </span>
          <span className="leading-relaxed">{s.text}</span>
        </li>
      ))}
    </ol>
  </div>
);
